import React, { useRef, useState, useEffect } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Share2, Download, X } from "lucide-react";
import { api, vibrate } from "@/lib/api";
import { APP_NAME, APP_DOMAIN } from "@/config";

const CARD_W = 360;
const CARD_H = 640;

function cardData(vertical, r) {
  if (vertical === "rizz_score") {
    return {
      tag: "RIZZ SCORE",
      big: r.grade || "?",
      score: r.score,
      scoreLabel: "/100",
      verdict: r.verdict,
      accent: "#ff007f",
    };
  }
  if (vertical === "red_flags") {
    const n = (r.flags || []).length;
    return {
      tag: "RED FLAGS CHECK",
      big: String(r.score ?? "?"),
      score: n,
      scoreLabel: n > 1 ? "red flags" : "red flag",
      verdict: r.verdict,
      accent: r.score < 40 ? "#ef4444" : r.score < 70 ? "#f59e0b" : "#10b981",
      flags: (r.flags || []).slice(0, 2),
    };
  }
  return {
    tag: "DM DOCTOR",
    big: "✓",
    score: null,
    scoreLabel: "",
    verdict: r.verdict || "Analyse terminée",
    accent: "#ff007f",
  };
}

export default function ShareResult({ vertical, result }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refCode, setRefCode] = useState(null);
  const cardRef = useRef(null);
  const d = cardData(vertical, result);

  useEffect(() => {
    if (open && !refCode) {
      api.get("/referral/me").then((r) => setRefCode(r.data.code)).catch(() => {});
    }
  }, [open, refCode]);

  const render = async () => {
    if (!cardRef.current) return null;
    return await toPng(cardRef.current, {
      cacheBust: true,
      pixelRatio: 2,
      width: CARD_W,
      height: CARD_H,
      style: { transform: "none" },
    });
  };

  const download = async () => {
    vibrate();
    setBusy(true);
    try {
      const url = await render();
      const a = document.createElement("a");
      a.href = url;
      a.download = "dm-doctor.png";
      a.click();
      toast.success("Carte téléchargée 🔥");
    } catch (e) {
      toast.error("Échec du téléchargement");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    vibrate([100, 50, 100]);
    setBusy(true);
    try {
      const url = await render();
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], "dm-doctor.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: APP_NAME,
          text: "J'ai fait analyser ma conv sur " + APP_NAME + " 👀",
        });
      } else {
        await download();
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Partage indisponible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="share-result-button"
        onClick={() => { vibrate(); setOpen(true); }}
        className="w-full flex items-center justify-center gap-2 rounded-full bg-zinc-800 text-white font-semibold py-3 hover:bg-zinc-700 transition-colors border border-zinc-700"
      >
        <Share2 size={16} /> Partager mon résultat
      </button>

      {open && (
        <div
          data-testid="share-modal"
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/85 backdrop-blur-sm px-6"
          onClick={() => setOpen(false)}
        >
          <button
            data-testid="share-close"
            onClick={() => setOpen(false)}
            className="absolute top-6 right-6 text-zinc-400 hover:text-white"
          >
            <X size={24} />
          </button>

          <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center gap-5">
            {/* The shareable card */}
            <div
              ref={cardRef}
              style={{ width: CARD_W, height: CARD_H }}
              className="relative overflow-hidden rounded-3xl"
            >
              <div style={{ position: "absolute", inset: 0, background: "#09090b" }} />
              <div
                style={{
                  position: "absolute",
                  top: -120, left: -80, width: 320, height: 320, borderRadius: 9999,
                  background: d.accent, opacity: 0.25, filter: "blur(90px)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: -140, right: -80, width: 320, height: 320, borderRadius: 9999,
                  background: "#8a2be2", opacity: 0.25, filter: "blur(90px)",
                }}
              />
              <div className="relative h-full flex flex-col p-8">
                <div className="flex items-center gap-2">
                  <img src="/icons/icon-512.png" alt="" className="w-8 h-8 rounded-lg" />
                  <span className="font-display font-bold text-white text-lg">{APP_NAME}</span>
                </div>

                <div
                  className="mt-6 inline-flex self-start text-[11px] font-bold tracking-[0.25em] px-3 py-1 rounded-full"
                  style={{ color: d.accent, border: `1px solid ${d.accent}66` }}
                >
                  {d.tag}
                </div>

                <div className="flex-1 flex flex-col items-center justify-center text-center">
                  <div
                    className="font-display font-black leading-none"
                    style={{ fontSize: 120, color: d.accent, textShadow: `0 0 40px ${d.accent}80` }}
                  >
                    {d.big}
                  </div>
                  {d.score !== null && (
                    <div className="text-zinc-400 text-sm mt-1 font-semibold">
                      {d.score} {d.scoreLabel}
                    </div>
                  )}
                  <p className="font-display text-white text-2xl font-bold leading-tight mt-6 px-2">
                    {d.verdict}
                  </p>
                  {d.flags && d.flags.length > 0 && (
                    <div className="mt-5 w-full space-y-2">
                      {d.flags.map((f, i) => (
                        <div key={i} className="text-left rounded-xl bg-white/5 px-3 py-2">
                          <p className="text-white text-sm font-semibold">🚩 {f.title}</p>
                          {f.evidence && (
                            <p
                              className="text-xs mt-0.5"
                              style={{ color: "transparent", textShadow: "0 0 7px rgba(255,255,255,0.85)" }}
                            >
                              “{f.evidence}”
                            </p>
                          )}
                        </div>
                      ))}
                      <p className="text-[10px] text-zinc-500">Citations floutées pour ta vie privée</p>
                    </div>
                  )}
                </div>

                <div className="text-center">
                  <p className="text-zinc-500 text-sm">Analyse ta conv sur</p>
                  <p className="font-display font-bold text-white text-lg">{APP_DOMAIN}</p>
                  {refCode && (
                    <div
                      className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1 rounded-full"
                      style={{ color: "#ff007f", border: "1px solid #ff007f66" }}
                    >
                      CODE {refCode} → 1 MOIS OFFERT
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 w-[360px]">
              <button
                data-testid="share-download"
                onClick={download}
                disabled={busy}
                className="flex-1 flex items-center justify-center gap-2 rounded-full bg-zinc-800 text-white font-semibold py-3 hover:bg-zinc-700 transition-colors disabled:opacity-60"
              >
                <Download size={16} /> Télécharger
              </button>
              <button
                data-testid="share-native"
                onClick={share}
                disabled={busy}
                className="flex-1 flex items-center justify-center gap-2 rounded-full bg-pink-500 text-white font-bold py-3 hover:bg-pink-600 transition-colors glow-pink disabled:opacity-60"
              >
                <Share2 size={16} /> Partager
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
